import classNames from "classnames";
import { DateTimeFormatter, LocalDateTime } from "@js-joda/core";
import React from "react";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { CALENDAR_SVG, LOCK_SVG } from "../utils/svgs";
import {
  AppCard,
  AppCardContent,
  AppCardHeader,
  AppCardSubtitle,
  AppCardTitle,
} from "./Generic/AppCard";
import { AppIcon } from "./Generic/AppIcon";
import { If } from "./Generic/If";
import { IsDefined } from "./Generic/IsDefined";
import { openDatepicker } from "./MedflytDatepicker";
import { RequiredDocAnswerableQuestionProps } from "./RequiredDocAnswerableQuestion";
import RequiredDocReadonlyAnswer from "./RequiredDocReadonlyAnswer";

const formatterLocalDateTime = DateTimeFormatter.ofPattern("MM/dd/yyyy','H:mm");

const RequiredDocDateTime = ({
  questionId,
  questionText,
  currentAnswer,
  notes,
  isRequired,
  readonly,
  isValid,
  handleOnInsertAnswer,
  onClickWhileDisabled,
}: RequiredDocAnswerableQuestionProps) => {
  const handleChangeDateTime = React.useCallback(
    (date?: LocalDateTime) => {
      if (date !== undefined) {
        handleOnInsertAnswer(questionId, date.toString());
      }
    },
    [handleOnInsertAnswer, questionId]
  );

  const handleOpenDatePicker = React.useCallback(() => {
    openDatepicker(
      {
        type: "datetime",
        callbackObj: { callback: (data) => handleChangeDateTime(data), cancelable: true },
      },
      translate(STRINGS.SELECT_DATE)
    );
  }, [handleChangeDateTime]);

  const handleClickWhileDisabled = React.useCallback(() => {
    if (readonly && onClickWhileDisabled !== undefined) {
      onClickWhileDisabled();
    }
  }, [readonly, onClickWhileDisabled]);

  return (
    <AppCard
      className={classNames({ __invalid: !isValid })}
      onClick={() => handleClickWhileDisabled}
    >
      <AppCardHeader>
        <AppCardTitle className="flex justify-space-between full-width">
          <>
            {questionText}
            <If condition={isRequired && !readonly}>
              <span className="__badge">{translate(STRINGS.REQUIRED)}</span>
            </If>
          </>
          <If condition={readonly}>
            <AppIcon icon={LOCK_SVG} fill="gray" size={16} />
          </If>
        </AppCardTitle>
        <IsDefined value={notes}>
          {(_notes) => (
            <AppCardSubtitle>{`${translate(STRINGS.NOTES)}: ${_notes}`}</AppCardSubtitle>
          )}
        </IsDefined>
      </AppCardHeader>
      <AppCardContent>
        {readonly ? (
          <RequiredDocReadonlyAnswer currentAnswer={currentAnswer} />
        ) : (
          <div className="RequiredDocDateTime" onClick={() => handleOpenDatePicker()}>
            {typeof currentAnswer === "string" ? (
              <span>{LocalDateTime.parse(currentAnswer).format(formatterLocalDateTime)}</span>
            ) : (
              translate(STRINGS.SELECT_DATE)
            )}
            <AppIcon size={24} className="__icon" icon={CALENDAR_SVG} />
          </div>
        )}
      </AppCardContent>
    </AppCard>
  );
};

export default React.memo(RequiredDocDateTime);
